<template>
  <div class="main-content">
    <div id="header" class="d-flex flex-row justify-content-between">
      <div id="title" class="align-self-center">
        <h1>{{ $t("sidebar.menu.accounting") }}</h1>
      </div>
      <div id="button-download">
        <LoadingButton
          :text="$t('utils.button.download-csv')"
          :classes="['btn-primary']"
          :loading="loading"
        />
      </div>
    </div>
    <div class="d-flex flex-row justify-content-between align-self-center">
    <div
      id="filter-container"
      class="d-flex flex-column justify-content-start mt-5 gap-2"
    >
      <div id="data-filter">
        <input
          name="date"
          type="text"
          onmouseover="(this.type='date')"
          onmouseleave="if(!this.value)(this.type='text')"
          onfocus="(this.type='date')"
          onblur="if(!this.value)this.type='text'"
          v-model="startDateFilter"
          :placeholder="$t('utils.placeholder.start-date')"
          @change="getPayments"
        />
      </div>
      <div id="data-filter">
        <input
          name="date"
          type="text"
          onmouseover="(this.type='date')"
          onmouseleave="if(!this.value)(this.type='text')"
          onfocus="(this.type='date')"
          onblur="if(!this.value)this.type='text'"
          v-model="endDateFilter"
          :placeholder="$t('utils.placeholder.end-date')"
          @change="getPayments"
        />
      </div>
    </div>
    <div class="d-flex flex-row justify-content-between align-self-center mt-5 amount">
      <div class="container-amount">
        <h2>{{$t('utils.space-amount')}}</h2>
        <p>{{totalAmount}} {{currency}}</p>
      </div>
      <div class="container-amount">
        <h2>{{$t('utils.fee')}} ({{(fee*100).toFixed(2)}}%)</h2>
        <p>{{feeAmount}} {{currency}}</p>
      </div>
      <div class="container-amount">
        <h2>{{$t('utils.total-amount')}}</h2>
        <p><b>{{spaceTotal}} {{currency}}</b></p>
      </div>
    </div>
    </div>
    <div class="mt-4">
      <b-table
        striped
        hover
        :items="accounting"
        :fields="fields"
        :busy="loading"
        @sort-changed="sort()"
      >
        <template #table-busy>
          <div class="text-center text-secondary my-2">
            <b-spinner class="align-middle"></b-spinner>
          </div>
        </template>
      </b-table>
    </div>

    <div class="pagination d-flex align-items-center justify-content-end p-4">
      <button
        class="btn d-flex align-items-center"
        @click="changePage(false)"
        :disabled="currentPage === 1 || loading"
      >
        <img
          src="@/assets/images/icons/arrow-select.svg"
          alt="Previous page"
          class="prev"
        />
      </button>
      <button
        class="btn d-flex align-items-center"
        @click="changePage(true)"
        :disabled="disableNextPage || loading"
      >
        <img
          src="@/assets/images/icons/arrow-select.svg"
          alt="Next page"
          class="next"
        />
      </button>
    </div>

    <MessageBanner
      :message="error"
      :type="'error'"
      :classes="['position-bottom-center']"
      v-if="error !== ''"
    />
  </div>
</template>

<script>
import LoadingButton from "@/components/utils/LoadingButton";
import PaymentsService from "@/services/payments";
import { mapGetters } from "vuex";

export default {
  components: {
    LoadingButton
  },
  data() {
    return {
      startDateFilter: "",
      endDateFilter: "",
      type: "",
      status: "null",
      fields: [
        {
          key: "buyer",
          label: this.$t("utils.table.buyer"),
          tdClass: "name",
          sortable: false
        },
        {
          key: "when",
          label: this.$t("utils.table.when"),
          sortable: false
        },
        {
          key: "seat_type",
          label: this.$t("utils.table.seat-type"),
          sortable: false
        },
        {
          key: "time",
          label: this.$t("utils.table.amount"),
          sortable: false
        },
        {
          key: "amount",
          label: this.$t('utils.space-amount'),
          sortable: false
        },
        {
          key: "fee",
          label: this.$t('utils.fee'),
          sortable: false
        },
        {
          key: "total_amount",
          label: this.$t('utils.total-amount'),
          sortable: false
        }
      ],
      items: [],
      accounting: [],
      loading: false,
      currentPage: 1,
      error: "",
      busy: false,
      disableNextPage: false,
      sortDirection: "asc",
      totalAmount: "",
      feeAmount: "",
      spaceTotal: "",
      fee: 0,
      currency: ""
    };
  },
  computed: {
    ...mapGetters("space", ["status"])
  },
  mounted() {
    if (!this.status || this.status === "draft" || this.status === "rejected") {
      this.$router.push({ name: "WelcomePage" });
    } else {
      this.getPayments();
    }
  },
  methods: {
    async getPayments() {
      this.loading = true;

      let startDate =
        this.startDateFilter !== ""
          ? new Date(this.startDateFilter).toISOString()
          : null;

      let endDate =
        this.endDateFilter !== ""
          ? new Date(this.endDateFilter).toISOString()
          : null;

      try {
        const filters = {
          startDate,
          endDate,
          orderDirection: this.sortDirection,
          orderBy: "bookingDate"
        };
        const { data } = await PaymentsService.getPayments(
          filters,
          this.currentPage
        );

        this.items = data.data;
        this.accounting = this.mapBookings(this.items);
        this.getTotalAmount(this.items)
        this.disableNextPage = this.items.length !== data.pagination.pagelen;
      } catch (error) {
        this.error = error;
        setTimeout(() => {
          this.error = "";
        }, 2500);
      } finally {
        this.loading = false;
        this.busy = false;
      }
    },
    mapBookings(data) {
      return data.map(payment => ({
        id: payment.id,
        buyer: payment.user.name,
        seat_type: this.mapSeatType(payment.product.type),
        when: this.setBookingPeriod(payment),
        amount: payment.amount + (payment.currency === "eur" ? '€' : '£'),
        time: payment.quantity,
        fee: payment.fee + (payment.currency === "eur" ? '€' : '£'),
        total_amount: payment.total + (payment.currency === "eur" ? '€' : '£')
      }));
    },
    getTotalAmount(data) {
    this.fee = data[0].space.fee;
    this.currency = (data[0].currency === "eur" ? '€' : '£')
    this.totalAmount = data.map((payment) => payment.amount).reduce((acc, currValue) => acc + currValue, 0);
    this.feeAmount = data.map((payment) => payment.fee).reduce((acc, currValue) => acc + currValue, 0);
    this.spaceTotal = data.map((payment) => payment.total).reduce((acc, currValue) => acc + currValue, 0);
    },
    setBookingPeriod(payment) {
      return `
      ${this.$moment(payment.bookingDate).format("DD/MM/Y")} ${this.$moment(
        payment.bookingDate
      ).format("HH:mm")}
      / ${this.$moment(payment.bookingDate)
        .add(payment.quantity, "h")
        .format("HH:mm")}`;
    },
    mapSeatType(seatType) {
      if (seatType === "easy_chair") {
        return this.$t("dashboard.seats.edit.easy-chair");
      } else if (seatType === "private_desk") {
        return this.$t("dashboard.seats.edit.private-desk");
      } else if (seatType === "meeting_room") {
        return this.$t("dashboard.seats.edit.meeting-rooms");
      }
    },
    sort() {
      if (this.sortDirection === "asc") {
        this.sortDirection = "desc";
      } else {
        this.sortDirection = "asc";
      }

      this.currentPage = 1;
      this.items = [];

      this.getPayments();
    },
    changePage(isNext) {
      if (isNext) {
        this.currentPage++;
      } else {
        this.currentPage--;
      }

      this.getPayments();
    }
  }
};
</script>

<style lang="scss" scoped>
@import "src/assets/scss/fonts";
@import "src/assets/scss/filters";
@import "src/assets/scss/buttons";

.main-content {
  #header {
    #title {
      h1 {
        font-family: $mulish;
        font-style: normal;
        font-weight: bold;
        font-size: 24px;
        line-height: 30px;
        letter-spacing: 0.3px;
        color: #252733;
      }
    }

    #button-download {
      button {
        width: 309px;
      }
    }
  }

  .amount {
    width: 70%;
    .container-amount {
    width: 258px;
    height: 100px;
    background: #EEF0F8;
    border-radius: 8px;

    h2 {
      padding: 5% 0 0 6%;
      font-family: $mulish;
      font-style: normal;
      font-weight: 600;
      font-size: 13px;
      line-height: 16px;
      letter-spacing: 0.3px;
      color: #8E9098;
    }

    p {
      padding: 1% 0 0 7%;
      font-family: $mulish;
      font-style: normal;
      font-weight: normal;
      font-size: 40px;
      line-height: 50px;
      letter-spacing: 0.3px;
      color: #252733;
    }
  }
  }

  .pagination {
    button {
      .prev {
        transform: rotate(90deg);
      }
      .next {
        transform: rotate(-90deg);
      }
      &:focus {
        box-shadow: none;
      }
    }
  }
}
</style>

<style lang="scss">
@import "src/assets/scss/components/table";
</style>



// WEBPACK FOOTER //
// src/pages/accounting/AccountingPage.vue